import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Download, FileText, Star, BookOpen, Users, TrendingUp, CheckCircle } from "lucide-react";
import CompanyLayout from "@/components/CompanyLayout";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import G2RecognitionSection from "@/components/G2RecognitionSection";
import whitepaperCover from "@/assets/whitepaper-manager-intelligence-cover.png";

interface Whitepaper {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  highlights: string[];
  category: string;
  downloadUrl?: string;
  available: boolean;
}

const whitepapers: Whitepaper[] = [
  {
    id: "1",
    slug: "manager-intelligence",
    title: "Manager Intelligence 2.0",
    subtitle: "The Real-Time Data Layer for People Leaders",
    description: "Learn how manager intelligence is revolutionizing how leaders understand and support their teams. This white paper explores the shift from annual surveys to real-time insights.",
    highlights: [
      "Why traditional engagement metrics fall short",
      "How real-time data empowers managers",
      "Case studies from high-performing organizations",
      "Implementation roadmap for your team"
    ],
    category: "Manager Effectiveness",
    available: true
  }
];

const WhitepapersHub = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    jobTitle: '',
    companySize: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('submitting');
    
    // Simulate form submission - in production, integrate with HubSpot
    await new Promise(resolve => setTimeout(resolve, 1500));
    setFormState('success');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <CompanyLayout
      title="White Papers & Research Reports"
      description="Download expert research and insights on employee engagement, manager effectiveness, and building high-performing teams."
    >
      <div className="bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-gray-50 via-white to-purple-50 pt-24 md:pt-32 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-20 left-10 w-20 h-20 bg-purple-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-32 h-32 bg-blue-500 rounded-full blur-3xl"></div>
          </div>

          <div className="container mx-auto px-4 max-w-7xl text-center relative z-10">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <BookOpen className="h-4 w-4" />
              Assembly Research and Insights
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Insights that cut through noise, reveal your team's truth
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
              Data-driven research to help leaders make smarter, more human decisions about their teams.
            </p>
            
            {/* G2 Rating */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 items-center justify-center">
              <div className="flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                <span className="text-sm font-semibold text-gray-700">4.9 / 5 on G2</span>
                <img src="/lovable-uploads/e68d106f-69a9-45e8-8624-7f09b1ae1e6f.png" alt="G2 Logo" className="h-5 w-5" />
              </div>
              <a href="https://www.g2.com/products/assembly-2021-12-09/reviews" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                <span className="text-sm font-semibold text-gray-500">3000+ reviews</span>
              </a>
            </div>
          </div>
        </section>

        {/* CTA Bar */}
        <section className="py-12 bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="text-center lg:text-left max-w-2xl">
                <h3 className="text-white text-xl md:text-2xl font-bold mb-2">
                  Want to see these insights applied to your own team?
                </h3>
                <p className="text-white/90 text-base">
                  Explore the real-time recognition analytics from the white papers and discover how Assembly instantly uncovers your top contributors, culture shifts, and emerging leaders. See it live for your team.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  variant="secondary" 
                  className="bg-white text-blue-600 hover:bg-gray-100"
                  onClick={() => setIsDemoModalOpen(true)}
                >
                  Book a Free Demo
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  className="border-2 border-white text-white bg-transparent hover:bg-white/20"
                  onClick={() => document.getElementById('featured-whitepaper')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Continue to Free Download
                  <Download className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Whitepaper Section */}
        <section id="featured-whitepaper" className="py-20 bg-white">
          <div className="container mx-auto px-4 max-w-7xl">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              Featured Research
            </h2>
            
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-3xl p-8 md:p-12 border border-purple-100">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
                    <FileText className="h-4 w-4" />
                    {whitepapers[0].category}
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                    {whitepapers[0].title}
                  </h3>
                  <p className="text-xl text-purple-600 font-medium mb-6">
                    {whitepapers[0].subtitle}
                  </p>
                  <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                    {whitepapers[0].description}
                  </p>
                  
                  <div className="space-y-3 mb-8">
                    <p className="font-semibold text-gray-900">What you'll learn:</p>
                    {whitepapers[0].highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{highlight}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button 
                    asChild
                    size="lg"
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  >
                    <a href={`/resources/whitepapers/${whitepapers[0].slug}`}>
                      Download White Paper
                      <Download className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </div>
                
                {/* Whitepaper Cover Image */}
                <div className="flex justify-center lg:justify-end">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-blue-500/10 rounded-2xl blur-2xl transform scale-105"></div>
                    <img 
                      src={whitepaperCover} 
                      alt="Manager Intelligence 2.0 White Paper"
                      className="relative w-full max-w-md rounded-2xl shadow-2xl border border-gray-200"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* G2 Recognition */}
        <G2RecognitionSection />

        {/* All Whitepapers Grid */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 max-w-7xl">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              All Research & White Papers
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {whitepapers.map((paper) => (
                <a 
                  key={paper.id}
                  href={`/resources/whitepapers/${paper.slug}`}
                  className="block bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden hover:shadow-xl hover:border-purple-200 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="aspect-[16/10] bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center overflow-hidden">
                    <img 
                      src={whitepaperCover} 
                      alt={paper.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="inline-flex items-center gap-1 bg-purple-50 text-purple-600 px-2 py-1 rounded text-xs font-medium mb-3">
                      {paper.category}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{paper.title}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{paper.description}</p>
                    <span className="inline-flex items-center text-purple-600 font-medium group">
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Footer */}
        <section className="py-20 bg-gradient-to-br from-purple-600 via-blue-600 to-blue-700 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxIDAgNiAyLjY5IDYgNnMtMi42OSA2LTYgNi02LTIuNjktNi02IDIuNjktNiA2LTZ6TTI0IDQyYzMuMzEgMCA2IDIuNjkgNiA2cy0yLjY5IDYtNiA2LTYtMi42OS02LTYgMi42OS02IDYtNnoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-30"></div>
          <div className="container mx-auto px-4 max-w-7xl text-center relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to transform your team's engagement?
            </h2>
            <p className="text-xl text-white/90 mb-10">
              See how Assembly helps organizations build thriving workplace cultures.
            </p>
            <div className="flex justify-center">
              <Button 
                size="lg" 
                variant="secondary" 
                className="group"
                onClick={() => setIsDemoModalOpen(true)}
              >
                Book a Demo
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          </div>
        </section>
      </div>
      
      <DemoBookingModal 
        open={isDemoModalOpen} 
        onOpenChange={setIsDemoModalOpen}
      />
    </CompanyLayout>
  );
};

export default WhitepapersHub;
